import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import type { Editor } from '@tiptap/react';
import { useTranslation } from 'next-i18next';
import * as React from 'react';
import { useCallback } from 'react';

export interface HeadingButtonProps {
  editor: Editor;
}

/**
 * A button component that allows the user to select a heading level for a text editor.
 * @param editor The text editor instance to apply the heading level to.
 * @returns A React component that renders a button with a dropdown menu of heading level options.
 */
export function HeadingButton({ editor }: HeadingButtonProps) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const options = [
    { label: t('Normal'), value: 'normal' },
    { label: t('Heading 1'), value: 'heading1' },
    { label: t('Heading 2'), value: 'heading2' },
    { label: t('Heading 3'), value: 'heading3' },
    { label: t('Heading 4'), value: 'heading4' },
    { label: t('Heading 5'), value: 'heading5' },
    { label: t('Heading 6'), value: 'heading6' },
  ] as const;

  const getActiveHeading = useCallback(() => {
    if (editor?.isActive('heading')) {
      const { level } = editor.getAttributes('heading');
      return level;
    } else {
      return 0;
    }
  }, [editor]);

  const activeHeading = getActiveHeading();

  const handleClick = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number,
  ) => {
    const option = options[index];
    setHeading(option);
    setOpen(false);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  return (
    <React.Fragment>
      <Button
        variant="contained"
        aria-haspopup="menu"
        ref={anchorRef}
        onClick={handleClick}
        endIcon={<ArrowDropDownIcon />}
        sx={{ minWidth: '140px' }}
      >
        {options[activeHeading].label}
      </Button>
      <Popper
        sx={{
          zIndex: 1,
        }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {options.map((option, index) => (
                    <MenuItem
                      key={option.value}
                      disabled={index === activeHeading}
                      selected={index === activeHeading}
                      onClick={(event) => handleMenuItemClick(event, index)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );

  function setHeading(option: (typeof options)[number]) {
    switch (option.value) {
      case 'normal':
        editor.chain().focus().setParagraph().run();
        break;
      case 'heading1':
        editor.chain().focus().toggleHeading({ level: 1 }).run();
        break;
      case 'heading2':
        editor.chain().focus().toggleHeading({ level: 2 }).run();
        break;
      case 'heading3':
        editor.chain().focus().toggleHeading({ level: 3 }).run();
        break;
      case 'heading4':
        editor.chain().focus().toggleHeading({ level: 4 }).run();
        break;
      case 'heading5':
        editor.chain().focus().toggleHeading({ level: 5 }).run();
        break;
      case 'heading6':
        editor.chain().focus().toggleHeading({ level: 6 }).run();
        break;
    }
  }
}

export default HeadingButton;
